import { useMemo, useState } from "react";
import { useContactChannel } from "../../hooks/useContactChannel";
import type { PatientDetailData } from "../../hooks/patients/usePatientDetail";
import type { EmergencyContact } from "./InfoTab";
import { AlertIcon, MeetModal, meetBtn } from "./MeetModal";
import { buildUrgencyMessage } from "./meetRules";

/**
 * Aviso de urgencia a CX con los datos del paciente, sin cortar la llamada. Solo arma el texto: no se envía nada solo ni se marca nada;
 * la psicóloga lo copia o lo abre en el canal de CX que le corresponde.
 */
export function UrgencyDialog({
  open,
  onClose,
  patient,
  contacts,
  sessionStart,
}: {
  open: boolean;
  onClose: () => void;
  patient: PatientDetailData | null;
  contacts: EmergencyContact[];
  sessionStart: string;
}) {
  const channel = useContactChannel();
  const [copied, setCopied] = useState(false);
  const message = useMemo(
    () =>
      buildUrgencyMessage({
        patientName: patient?.fullname || "Paciente",
        country: patient?.profile?.country,
        timezone: patient?.profile?.timezone,
        phone: patient?.phone,
        contacts,
        sessionStart,
      }),
    [patient, contacts, sessionStart],
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <MeetModal
      open={open}
      onClose={onClose}
      wide
      icon={<AlertIcon />}
      title="Aviso de urgencia a CX"
      actions={
        <>
          <button className={meetBtn.ghost} onClick={onClose}>
            Cerrar
          </button>
          <button className={meetBtn.secondary} onClick={() => void copy()}>
            {copied ? "Copiado" : "Copiar texto"}
          </button>
          <a
            className={`${meetBtn.secondary} inline-block`}
            href={`mailto:${channel.email}?subject=${encodeURIComponent("URGENCIA en sesión")}&body=${encodeURIComponent(message)}`}
          >
            Mail a CX
          </a>
          <a
            className={`${meetBtn.danger} inline-block px-5 py-2.5`}
            target="_blank"
            rel="noreferrer"
            href={`https://wa.me/${channel.whatsappNumber}?text=${encodeURIComponent(message)}`}
          >
            WhatsApp de CX
          </a>
        </>
      }
    >
      <p className="mb-3">
        La llamada sigue abierta. Revisá el texto y envialo por el canal de CX.
      </p>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-2xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
        {message}
      </pre>
    </MeetModal>
  );
}
